import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Badge,
  TextInput,
  Textarea,
  Select,
  SegmentedControl,
  Alert,
  Loader,
  ActionIcon,
  Tooltip,
  Code,
  Divider,
  Box,
  CopyButton,
  FileInput,
  Image
} from '@mantine/core';
import {
  IconAlertCircle,
  IconPlus,
  IconTrash,
  IconRobot,
  IconDownload,
  IconCheck,
  IconCopy,
  IconRefresh,
  IconShieldCheck,
  IconPhotoScan,
  IconX
} from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import PlatformBackButton from '../../components/PlatformBackButton';
import {
  submitAgentJob,
  pollAgentJob,
  isAgentStudioBackendConfigured
} from './api_scripts/agentStudioClient';
import { callQualityJudge } from '../../shared/services/qualityJudgeService';
import JudgeProgressModal from '../../components/JudgeProgressModal';
import { HeaderBadge, RegenerateButton, FindingsAlert } from './components/AgentQualityBadge';

const PLATFORM_OPTIONS = [
  { value: 'wxcc', label: 'WebEx Contact Center (WxCC)', disabled: false },
  { value: 'lex', label: 'AWS Lex — coming soon', disabled: true },
  { value: 'genesys', label: 'Genesys — coming soon', disabled: true },
  { value: 'five9', label: 'Five9 — coming soon', disabled: true }
];

// PDFs accepted alongside images so a multi-page Lucid / draw.io / Visio
// export can drop in whole. Bedrock walks every page in a single document
// block; no client-side splitting.
const ACCEPTED_DIAGRAM_TYPES = 'image/png,image/jpeg,image/gif,image/webp,application/pdf';
// Raw file size cap — images stay around the original ~4MB ceiling, PDFs can
// go up to ~24MB raw (base64-encoded ~32MB matches Bedrock's document block
// cap). The Lambda re-validates at MAX_DIAGRAM_BASE64_BYTES = 32MB post-encode.
const MAX_DIAGRAM_BYTES = 24 * 1024 * 1024;

// Read a File into a base64 string (no data: prefix) so we can post it to the
// Bedrock-fronting Lambda as the `diagram.imageBase64` field.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = reader.result;
      const comma = typeof result === 'string' ? result.indexOf(',') : -1;
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

const PERSONA_OPTIONS = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'concise', label: 'Concise' },
  { value: 'clinical', label: 'Clinical' },
  { value: 'empathetic', label: 'Empathetic' }
];

function blankSlot() {
  return { name: '', description: '', examples: '' };
}
function blankAction() {
  return { name: '', description: '' };
}
function blankTransfer() {
  return { name: '', whenToUse: '' };
}

function downloadJson(filename, jsonObj) {
  const blob = new Blob([JSON.stringify(jsonObj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function AgentTemplateCreator({ onBackToPlatformSelector }) {
  const { getApiToken } = useAuth();
  const backendConfigured = isAgentStudioBackendConfigured();

  const [platform, setPlatform] = useState('wxcc');
  const [agentType, setAgentType] = useState('autonomous');
  // Scripted-only subtype. "single-turn" is the default (traditional
  // intent/slot bot with one entry, one exit). "shared-answer-roundtrip"
  // signals a bot that drives multi-turn Q&A via the flow using shared
  // AnswerYes/AnswerNo intents + last_question dispatch. "decision-tree"
  // is reserved for bots that own a full decision tree internally
  // (rare — usually done in-flow instead).
  const [scriptedSubtype, setScriptedSubtype] = useState('single-turn');
  const [channel, setChannel] = useState('voice');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [persona, setPersona] = useState('professional');
  const [slots, setSlots] = useState([]);
  const [actions, setActions] = useState([]);
  const [transferTargets, setTransferTargets] = useState([]);
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [diagramFile, setDiagramFile] = useState(null);
  const [diagramPreviewUrl, setDiagramPreviewUrl] = useState(null);
  const [diagramError, setDiagramError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null); // PENDING | RUNNING | SUCCEEDED | FAILED
  const [jobStartedAt, setJobStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0); // seconds since submit, for the progress label
  const resultsRef = useRef(null);
  const pollAbortRef = useRef(null);

  // Quality-judge advisory state. judgeRunIdRef guards against stale judge
  // responses if the user regenerates while a previous judge is still in
  // flight. lastFormPayloadRef holds the body shape used for the most recent
  // submit so handleRegenerate can refire with priorJudgeFindings spread in.
  const [judgeEvaluating, setJudgeEvaluating] = useState(false);
  const [qualityScore, setQualityScore] = useState(null);
  const [qualityFindings, setQualityFindings] = useState([]);
  const judgeRunIdRef = useRef(0);
  const lastFormPayloadRef = useRef(null);
  const standardsForJudgeRef = useRef(null);

  // Pull the freshly-generated agent JSON into view. The form above is tall
  // (8+ sections), so after submit the user is typically near the Generate
  // button at the bottom; without this they'd never see the result render.
  useEffect(() => {
    if (result?.json && resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [result]);

  // Clear the diagram when the user switches away from Scripted — the backend
  // only accepts diagrams for scripted bots.
  useEffect(() => {
    if (agentType !== 'scripted' && diagramFile) {
      setDiagramFile(null);
      setDiagramPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      setDiagramError(null);
    }
  }, [agentType, diagramFile]);

  useEffect(() => {
    return () => { if (diagramPreviewUrl) URL.revokeObjectURL(diagramPreviewUrl); };
  }, [diagramPreviewUrl]);

  const handleDiagramChange = (file) => {
    setDiagramError(null);
    if (diagramPreviewUrl) URL.revokeObjectURL(diagramPreviewUrl);
    if (!file) {
      setDiagramFile(null);
      setDiagramPreviewUrl(null);
      return;
    }
    if (file.size > MAX_DIAGRAM_BYTES) {
      setDiagramError(`Diagram is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 4 MB.`);
      setDiagramFile(null);
      setDiagramPreviewUrl(null);
      return;
    }
    setDiagramFile(file);
    setDiagramPreviewUrl(URL.createObjectURL(file));
  };

  // Poll the job once submitted. Runs every 2s until SUCCEEDED/FAILED or until
  // a guard timeout (5 min — well past any realistic Bedrock run). Cleans up
  // on unmount or when the user re-submits.
  useEffect(() => {
    if (!jobId || !jobStatus || jobStatus === 'SUCCEEDED' || jobStatus === 'FAILED') return;
    let cancelled = false;
    pollAbortRef.current = { cancel: () => { cancelled = true; } };
    const startedAt = jobStartedAt || Date.now();
    const POLL_INTERVAL_MS = 2000;
    const MAX_DURATION_MS = 5 * 60 * 1000;
    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_DURATION_MS) {
        if (!cancelled) {
          setError('Job timed out after 5 minutes. Check CloudWatch logs for the Lambda.');
          setSubmitting(false);
          setJobStatus('FAILED');
        }
        return;
      }
      try {
        const row = await pollAgentJob(jobId, getApiToken);
        if (cancelled) return;
        setJobStatus(row.status);
        if (row.status === 'SUCCEEDED') {
          setResult(row.result || null);
          setSubmitting(false);
          if (row.result?.json) {
            startJudge({
              agentJson: row.result.json,
              standards: row.result.standardsSnapshot,
              formInput: lastFormPayloadRef.current
            });
          }
          return;
        }
        if (row.status === 'FAILED') {
          const e = row.error || {};
          setError(e.message || `Job failed (code: ${e.code || 'UNKNOWN'})`);
          setSubmitting(false);
          return;
        }
        // PENDING or RUNNING — keep polling
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Polling failed.');
        setSubmitting(false);
        setJobStatus('FAILED');
      }
    };
    setTimeout(tick, POLL_INTERVAL_MS);
    return () => { cancelled = true; };
  }, [jobId, jobStatus, jobStartedAt, getApiToken]);

  // Tick the elapsed-seconds counter while a job is in flight. Cheap useEffect
  // that drives the "Generating… 12s" label so users see something moving.
  useEffect(() => {
    if (!jobStartedAt || jobStatus === 'SUCCEEDED' || jobStatus === 'FAILED') {
      setElapsed(0);
      return;
    }
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - jobStartedAt) / 1000)), 1000);
    return () => clearInterval(id);
  }, [jobStartedAt, jobStatus]);

  const filenameSafe = useMemo(() => {
    const slug = (name || 'agent')
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 64) || 'agent';
    return `${slug}.json`;
  }, [name]);

  const updateRow = (setter, idx, patch) => {
    setter((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };
  const removeRow = (setter, idx) => {
    setter((prev) => prev.filter((_, i) => i !== idx));
  };

  // Fires after polling resolves SUCCEEDED. Sends agentJson + form input +
  // channel-scoped standards snapshot to the quality-judge Lambda. Stale-guard
  // pattern matches the SIT importers: a regenerate-while-judging discards the
  // first judge's response when it lands after the second one has started.
  const startJudge = async ({ agentJson, standards, formInput }) => {
    if (!agentJson || !standards || !formInput) return;
    const runId = ++judgeRunIdRef.current;
    setJudgeEvaluating(true);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      const data = await callQualityJudge({
        kind: 'agentstudio-agent',
        formInput,
        agentJson,
        standards
      });
      if (judgeRunIdRef.current !== runId) return;
      if (typeof data?.overallScore === 'number') setQualityScore(data.overallScore);
      if (Array.isArray(data?.perItem)) setQualityFindings(data.perItem);
    } catch (e) {
      if (judgeRunIdRef.current !== runId) return;
      // Judge is advisory — silently drop the badge on failure.
      // eslint-disable-next-line no-console
      console.warn('quality-judge call failed:', e);
    } finally {
      if (judgeRunIdRef.current === runId) setJudgeEvaluating(false);
    }
  };

  const handleGenerate = async (priorJudgeFindings) => {
    if (!name.trim() || !description.trim()) {
      setError('Name and description are required.');
      return;
    }
    // Cancel any in-flight polling from a previous submit before starting a new one.
    if (pollAbortRef.current) pollAbortRef.current.cancel();
    setSubmitting(true);
    setError(null);
    setResult(null);
    setJobId(null);
    setJobStatus(null);
    setJobStartedAt(null);
    setQualityScore(null);
    setQualityFindings([]);
    try {
      let diagramPayload;
      if (agentType === 'scripted' && diagramFile) {
        const imageBase64 = await fileToBase64(diagramFile);
        diagramPayload = { imageBase64, mediaType: diagramFile.type };
      }
      const body = {
        platform,
        agentType,
        channel,
        ...(agentType === 'scripted' ? { scriptedSubtype } : {}),
        name: name.trim(),
        description: description.trim(),
        ...(diagramPayload ? { diagram: diagramPayload } : {}),
        ...(welcomeMessage.trim() ? { welcomeMessage: welcomeMessage.trim() } : {}),
        ...(persona ? { persona } : {}),
        ...(slots.length > 0
          ? {
              slots: slots
                .filter((s) => s.name.trim())
                .map((s) => ({
                  name: s.name.trim(),
                  description: s.description.trim() || undefined,
                  examples: s.examples.trim() || undefined
                }))
            }
          : {}),
        ...(actions.length > 0
          ? {
              actions: actions
                .filter((a) => a.name.trim())
                .map((a) => ({
                  name: a.name.trim(),
                  description: a.description.trim() || undefined
                }))
            }
          : {}),
        ...(transferTargets.length > 0
          ? {
              transferTargets: transferTargets
                .filter((t) => t.name.trim())
                .map((t) => ({
                  name: t.name.trim(),
                  whenToUse: t.whenToUse.trim() || undefined
                }))
            }
          : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(Array.isArray(priorJudgeFindings) && priorJudgeFindings.length > 0
          ? { priorJudgeFindings }
          : {})
      };
      // Remember the form-input portion of the body so the quality-judge can
      // grade against it later, and so a regenerate has the same shape to
      // refire with priorJudgeFindings spread in.
      lastFormPayloadRef.current = {
        platform, agentType, channel, name: body.name, description: body.description,
        ...(body.scriptedSubtype ? { scriptedSubtype: body.scriptedSubtype } : {}),
        ...(body.persona ? { persona: body.persona } : {}),
        ...(body.welcomeMessage ? { welcomeMessage: body.welcomeMessage } : {}),
        ...(body.slots ? { slots: body.slots } : {}),
        ...(body.actions ? { actions: body.actions } : {}),
        ...(body.transferTargets ? { transferTargets: body.transferTargets } : {}),
        ...(body.notes ? { notes: body.notes } : {})
      };
      const submitted = await submitAgentJob(body, getApiToken);
      setJobId(submitted.jobId);
      setJobStatus(submitted.status || 'PENDING');
      setJobStartedAt(Date.now());
      // submitting stays true; the polling useEffect will flip it off once
      // the job hits SUCCEEDED or FAILED.
    } catch (e) {
      setError(e.message || 'Generation failed.');
      setSubmitting(false);
    }
  };

  // Regenerate the agent with the judge's non-info findings appended to the
  // user prompt as an "Address these previous-iteration concerns" block.
  const handleRegenerate = (findings) => handleGenerate(findings);

  return (
    <Container size="xl" py="lg">
      <Group justify="space-between" mb="md">
        <PlatformBackButton onClick={onBackToPlatformSelector} color="cyan" mb={0} />
      </Group>

      <Stack gap="md">
        <Group gap="sm">
          <IconRobot size={28} color="var(--mantine-color-cyan-6)" />
          <Title order={2}>Agent Template Creator</Title>
          <Badge color="cyan" variant="light">v1 — WxCC</Badge>
        </Group>

        <Text c="dimmed">
          Describe the agent you want to build. We&apos;ll apply the platform&apos;s standards
          (data-collection patterns, error recovery, confirmation gates, escalation paths) and
          produce an <strong>importable JSON file</strong> you can load directly into the WxCC AI
          Agent Studio via its Import Agent feature.
        </Text>

        {!backendConfigured && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            AI Agent Studio API URL is not configured. Set
            <Code> REACT_APP_AGENT_STUDIO_API_BASE_URL </Code>
            in your environment to enable generation.
          </Alert>
        )}

        <Card withBorder shadow="sm" radius="md" p="lg">
          <Stack gap="md">
            <Group grow>
              <Select
                label="Target platform"
                description="Other platforms coming soon"
                data={PLATFORM_OPTIONS}
                value={platform}
                onChange={(v) => v && setPlatform(v)}
              />
              <div>
                <Text size="sm" fw={500} mb={4}>Agent type</Text>
                <SegmentedControl
                  fullWidth
                  value={agentType}
                  onChange={setAgentType}
                  data={[
                    { value: 'autonomous', label: 'Autonomous' },
                    { value: 'scripted', label: 'Scripted' }
                  ]}
                />
              </div>
            </Group>

            {agentType === 'scripted' && (
              <div>
                <Text size="sm" fw={500} mb={4}>Scripted subtype</Text>
                <Text size="xs" c="dimmed" mb={6}>
                  <b>Single-turn:</b> one entry, one exit (classic slot-fill bot).{' '}
                  <b>Shared-answer round-trip:</b> multi-turn Q&A driven by the flow —
                  shared AnswerYes / AnswerNo intents dispatched by <code>last_question</code>.{' '}
                  <b>Decision-tree:</b> bot owns a full internal decision tree (rare;
                  usually done in-flow instead).
                </Text>
                <SegmentedControl
                  fullWidth
                  value={scriptedSubtype}
                  onChange={setScriptedSubtype}
                  data={[
                    { value: 'single-turn', label: 'Single-turn' },
                    { value: 'shared-answer-roundtrip', label: 'Shared-answer round-trip' },
                    { value: 'decision-tree', label: 'Decision tree' }
                  ]}
                />
              </div>
            )}

            <div>
              <Text size="sm" fw={500} mb={4}>Channel</Text>
              <Text size="xs" c="dimmed" mb={6}>
                Voice = TTS-read phone call (spell-back, NATO phonetic, DTMF fallback).{' '}
                Digital = chat / SMS / email (restate-back, quick replies, message bubbles).
              </Text>
              <SegmentedControl
                fullWidth
                value={channel}
                onChange={setChannel}
                data={[
                  { value: 'voice', label: '☎️ Voice' },
                  { value: 'digital', label: '💬 Digital (chat / SMS / email)' }
                ]}
              />
            </div>

            {agentType === 'scripted' && (
              <>
                <Divider label="Flow diagram (optional, Scripted only)" labelPosition="left" />
                <Stack gap="xs">
                  <Text size="xs" c="dimmed">
                    Drop a call-flow diagram and the generator will use it as the structural ground truth
                    for intents, entities, and the response workflow graph. PNG / JPG / GIF / WebP, max 4 MB.
                    The form fields below still supply the persona, name, and behavior rules.
                  </Text>
                  <FileInput
                    leftSection={<IconPhotoScan size={16} />}
                    placeholder="Choose a diagram image"
                    accept={ACCEPTED_DIAGRAM_TYPES}
                    value={diagramFile}
                    onChange={handleDiagramChange}
                    clearable
                  />
                  {diagramError && (
                    <Alert color="red" icon={<IconAlertCircle size={16} />} onClose={() => setDiagramError(null)} withCloseButton>
                      {diagramError}
                    </Alert>
                  )}
                  {diagramPreviewUrl && (
                    <Box style={{ position: 'relative', maxWidth: 480 }}>
                      {diagramFile?.type === 'application/pdf' ? (
                        // <embed> uses the browser's bundled PDF viewer for
                        // multi-page navigation. Falls back to a name+size
                        // line above if the viewer is blocked.
                        <embed
                          src={diagramPreviewUrl}
                          type="application/pdf"
                          style={{
                            width: '100%',
                            height: 360,
                            border: '1px solid var(--mantine-color-gray-3)',
                            borderRadius: 4
                          }}
                          title={`${diagramFile.name} PDF preview`}
                        />
                      ) : (
                        <Image
                          src={diagramPreviewUrl}
                          alt="Diagram preview"
                          radius="sm"
                          style={{ border: '1px solid var(--mantine-color-gray-3)' }}
                        />
                      )}
                      <ActionIcon
                        color="red"
                        variant="filled"
                        size="sm"
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={() => handleDiagramChange(null)}
                        aria-label="Remove diagram"
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Box>
                  )}
                </Stack>
              </>
            )}

            <TextInput
              label="Agent name"
              description="Max 256 characters"
              placeholder="e.g. Acme Insurance — Claims Triage"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              maxLength={256}
              required
            />

            <Textarea
              label="What does this agent do?"
              description="The goal of the agent — keep it focused on outcomes for the caller."
              placeholder="e.g. Help insurance customers report a new auto claim, capture incident details, and route to the right adjuster."
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              autosize
              minRows={4}
              required
            />

            <Group grow align="flex-start">
              <Textarea
                label="Welcome message (optional)"
                description="First message spoken to the caller. We'll generate one if omitted."
                value={welcomeMessage}
                onChange={(e) => setWelcomeMessage(e.currentTarget.value)}
                autosize
                minRows={2}
              />
              <Select
                label="Tone / persona"
                data={PERSONA_OPTIONS}
                value={persona}
                onChange={(v) => v && setPersona(v)}
              />
            </Group>

            <Divider label="Slots to collect (optional)" labelPosition="left" />
            <Stack gap="xs">
              {slots.map((s, i) => (
                <Group key={i} wrap="nowrap" gap="xs" align="flex-start">
                  <TextInput
                    placeholder="Slot name (e.g. policy_number)"
                    value={s.name}
                    onChange={(e) => updateRow(setSlots, i, { name: e.currentTarget.value })}
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    placeholder="What to collect / how"
                    value={s.description}
                    onChange={(e) => updateRow(setSlots, i, { description: e.currentTarget.value })}
                    style={{ flex: 2 }}
                  />
                  <TextInput
                    placeholder="Example values"
                    value={s.examples}
                    onChange={(e) => updateRow(setSlots, i, { examples: e.currentTarget.value })}
                    style={{ flex: 1 }}
                  />
                  <ActionIcon color="red" variant="subtle" onClick={() => removeRow(setSlots, i)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Group>
                <Button
                  variant="subtle"
                  color="cyan"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setSlots((p) => [...p, blankSlot()])}
                >
                  Add slot
                </Button>
              </Group>
            </Stack>

            {agentType === 'autonomous' && (
              <>
                <Divider label="Actions the agent can take (optional)" labelPosition="left" />
                <Stack gap="xs">
                  {actions.map((a, i) => (
                    <Group key={i} wrap="nowrap" gap="xs" align="flex-start">
                      <TextInput
                        placeholder="Action name (e.g. file_claim)"
                        value={a.name}
                        onChange={(e) => updateRow(setActions, i, { name: e.currentTarget.value })}
                        style={{ flex: 1 }}
                      />
                      <TextInput
                        placeholder="What this action does and when to invoke it"
                        value={a.description}
                        onChange={(e) => updateRow(setActions, i, { description: e.currentTarget.value })}
                        style={{ flex: 3 }}
                      />
                      <ActionIcon color="red" variant="subtle" onClick={() => removeRow(setActions, i)}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                  <Group>
                    <Button
                      variant="subtle"
                      color="cyan"
                      size="xs"
                      leftSection={<IconPlus size={14} />}
                      onClick={() => setActions((p) => [...p, blankAction()])}
                      disabled={actions.length >= 10}
                    >
                      Add action {actions.length >= 10 && '(max 10)'}
                    </Button>
                  </Group>
                </Stack>
              </>
            )}

            <Divider label="Transfer destinations (optional)" labelPosition="left" />
            <Stack gap="xs">
              {transferTargets.map((t, i) => (
                <Group key={i} wrap="nowrap" gap="xs" align="flex-start">
                  <TextInput
                    placeholder="Destination (e.g. claims queue)"
                    value={t.name}
                    onChange={(e) => updateRow(setTransferTargets, i, { name: e.currentTarget.value })}
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    placeholder="When to transfer here"
                    value={t.whenToUse}
                    onChange={(e) => updateRow(setTransferTargets, i, { whenToUse: e.currentTarget.value })}
                    style={{ flex: 2 }}
                  />
                  <ActionIcon color="red" variant="subtle" onClick={() => removeRow(setTransferTargets, i)}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Group>
                <Button
                  variant="subtle"
                  color="cyan"
                  size="xs"
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setTransferTargets((p) => [...p, blankTransfer()])}
                >
                  Add transfer destination
                </Button>
              </Group>
            </Stack>

            <Textarea
              label="Additional notes (optional)"
              description="Anything else the LLM should know — edge cases, compliance asks, must-have phrases."
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
              autosize
              minRows={2}
            />

            {error && (
              <Alert color="red" icon={<IconAlertCircle size={16} />} onClose={() => setError(null)} withCloseButton>
                {error}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button
                color="cyan"
                leftSection={<IconRobot size={16} />}
                loading={submitting}
                disabled={!backendConfigured || !name.trim() || !description.trim()}
                onClick={() => handleGenerate()}
              >
                Generate agent JSON
              </Button>
            </Group>
          </Stack>
        </Card>

        {submitting && (
          <Group gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {jobStatus === 'RUNNING' && `Composing your agent on Bedrock… ${elapsed}s`}
              {jobStatus === 'PENDING' && `Submitted — worker starting up… ${elapsed}s`}
              {!jobStatus && 'Submitting…'}
            </Text>
            {jobId && (
              <Badge variant="light" color="gray" size="xs">job {jobId.slice(0, 8)}</Badge>
            )}
          </Group>
        )}

        {result?.json && (
          <div ref={resultsRef} style={{ scrollMarginTop: 16 }}>
          <Card withBorder shadow="sm" radius="md" p="lg">
            <Group justify="space-between" mb="sm" wrap="wrap">
              <Group gap="xs">
                <Title order={4}>Generated agent</Title>
                {result.model && <Badge variant="light" color="gray">{result.model}</Badge>}
                {typeof result.durationMs === 'number' && (
                  <Badge variant="light" color="gray">{(result.durationMs / 1000).toFixed(1)}s</Badge>
                )}
                {result.diagramUsed && (
                  <Badge variant="light" color="cyan" leftSection={<IconPhotoScan size={12} />}>
                    Diagram applied
                  </Badge>
                )}
                <HeaderBadge
                  evaluating={judgeEvaluating}
                  qualityScore={qualityScore}
                  color="cyan"
                />
              </Group>
              <Group gap="xs">
                <RegenerateButton
                  qualityFindings={qualityFindings}
                  onRegenerate={handleRegenerate}
                  color="cyan"
                />
                <CopyButton value={JSON.stringify(result.json, null, 2)}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy JSON'}>
                      <ActionIcon variant="subtle" onClick={copy}>
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
                <Button
                  variant="light"
                  color="cyan"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => downloadJson(filenameSafe, result.json)}
                >
                  Download {filenameSafe}
                </Button>
                <Button
                  variant="subtle"
                  color="cyan"
                  leftSection={<IconRefresh size={14} />}
                  onClick={() => handleGenerate()}
                  loading={submitting}
                >
                  Regenerate
                </Button>
              </Group>
            </Group>

            {Array.isArray(result.appliedStandards) && result.appliedStandards.length > 0 && (
              <Group gap="xs" mb="sm">
                <IconShieldCheck size={16} style={{ color: 'var(--mantine-color-cyan-6)' }} />
                <Text size="xs" c="dimmed">Applied standards:</Text>
                {result.appliedStandards.map((s) => (
                  <Badge key={s} size="xs" variant="dot" color="cyan">{s}</Badge>
                ))}
              </Group>
            )}

            <FindingsAlert qualityFindings={qualityFindings} color="cyan" />

            <Alert color="cyan" variant="light" mb="sm">
              <Text size="sm">
                <strong>Next:</strong> open WxCC AI Agent Studio, choose <strong>Import agent</strong>, and upload the
                downloaded JSON. Cisco does not publish the canonical import schema yet — if the import
                rejects a field, edit the JSON and try again. Tell us which field failed so we can refine
                the generator&apos;s standards.
              </Text>
            </Alert>

            <Box>
              <Code block style={{ maxHeight: 480, overflow: 'auto', fontSize: 11 }}>
                {JSON.stringify(result.json, null, 2)}
              </Code>
            </Box>
          </Card>
          </div>
        )}
      </Stack>

      <JudgeProgressModal
        opened={judgeEvaluating}
        onClose={() => { /* parent controls via judgeEvaluating flag */ }}
        kind="agentstudio-agent"
        color="cyan"
      />
    </Container>
  );
}

export default AgentTemplateCreator;
